import {
  AloftPrice,
  AloftPriceJSON,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  SensitivityAnalysisUnitRateResult,
  UnitRateModelFeatureKey,
  orUndefined,
  schAloftArea,
  schAloftPrice,
} from '@aloft/toolkit-shared';
import { Flex, Typography, theme } from '@aloft/toolkit-sui';

import { get } from 'lodash';

import { PROPERTY_GRID_ROW_HEIGHT } from '../consts.ts';
import { readOnlyFieldProps } from '../fields/styles.ts';
import { useSensitivityContext } from '../PropertyGridSensitivityContext.tsx';
import { PropertyGridSectionProps } from '../types.ts';

type NumericExtractor = (value: unknown) => number | undefined;

const varNumericExtractors: Record<UnitRateModelFeatureKey, NumericExtractor> = {
  gla: (v) => orUndefined(schAloftArea.parse)(v)?.toSqFt().unitAmount,
  bedrooms: (v) => (typeof v === 'number' ? v : undefined),
  fullBaths: (v) => (typeof v === 'number' ? v : undefined),
  halfBaths: (v) => (typeof v === 'number' ? v : undefined),
  garageCarport: (v) => {
    const imp = v as
      | {
          garage?: { spaces?: number };
          detachedGarage?: { spaces?: number };
          carport?: { spaces?: number };
        }
      | undefined;

    if (!imp) return undefined;
    return (
      (imp.garage?.spaces ?? 0) + (imp.detachedGarage?.spaces ?? 0) + (imp.carport?.spaces ?? 0)
    );
  },
  actualAge: (v) => (typeof v === 'number' ? v : undefined),
  siteSize: (v) => orUndefined(schAloftArea.parse)(v)?.toSqFt().unitAmount,
};

const featureFieldPaths: Record<UnitRateModelFeatureKey, string> = {
  gla: 'editableData.improvements.gla',
  bedrooms: 'editableData.improvements.bedrooms',
  fullBaths: 'editableData.improvements.fullBaths',
  halfBaths: 'editableData.improvements.halfBaths',
  garageCarport: 'editableData.improvements',
  actualAge: 'editableData.improvements.age',
  siteSize: 'editableData.site.area',
};

const formatDollars = (dollars: number) =>
  AloftPrice.fromDollarLike(Math.round(dollars)).toString(true);

const sharedCellSxBase = {
  ...readOnlyFieldProps(1).__dangerDoNotUseSx,
  height: PROPERTY_GRID_ROW_HEIGHT,
  overflow: 'hidden',
};

export const SensitivityRateSection = ({
  columnIndex,
  fieldTypes,
  getValues,
}: PropertyGridSectionProps) => {
  const { sensitivityResult, sensitivityFeatureKey } = useSensitivityContext();
  const featureKey: UnitRateModelFeatureKey = sensitivityFeatureKey ?? 'gla';

  const isLabelColumn = fieldTypes.includes('label');

  // Label column: show "Unit Rate" header + rate value per row
  if (isLabelColumn) {
    if (!sensitivityResult) return null;
    return (
      <>
        <Typography
          variant="label"
          __dangerDoNotUseSx={{ ...sharedCellSxBase, backgroundColor: theme.colors.surface7 }}
        >
          Unit Rate
        </Typography>
        {sensitivityResult.testedUnitRates.map((entry) => {
          const isSelected =
            entry.rate?.unitAmount === sensitivityResult.selectedRate.rate?.unitAmount;
          const bg = isSelected ? theme.colors.surface8 : theme.colors.surface1;
          const rateDollars = orUndefined((v: AloftPriceJSON) => schAloftPrice.parse(v))(
            entry.rate,
          )?.toDollarLike();
          return (
            <Typography
              key={entry.rate?.unitAmount}
              variant="body1"
              __dangerDoNotUseSx={{ ...sharedCellSxBase, backgroundColor: bg }}
            >
              {rateDollars !== undefined ? formatDollars(rateDollars) : '—'}
            </Typography>
          );
        })}
      </>
    );
  }

  const propertyId = getValues(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  const propertyData = getValues(`columns.${columnIndex}.propertyData`);
  const fieldPath = featureFieldPaths[featureKey];
  const extractor = varNumericExtractors[featureKey];

  const salePriceRaw = get(propertyData, 'editableData.transaction.price') as
    | AloftPriceJSON
    | undefined;
  const salePriceDollars = orUndefined(schAloftPrice.parse)(salePriceRaw)?.toDollarLike();
  const varValue = extractor(get(propertyData, fieldPath));

  const subjectData = getValues('columns.0.propertyData');
  const subjectVarValue = extractor(get(subjectData, fieldPath));

  const selectedRateUnitAmount = sensitivityResult?.selectedRate.rate?.unitAmount;

  if (!sensitivityResult) return null;

  const renderSubjectRow = (entry: SensitivityAnalysisUnitRateResult, isSelected: boolean) => {
    const bg = isSelected ? theme.colors.surface8 : theme.colors.surface1;
    return (
      <Flex
        key={entry.rate?.unitAmount}
        __dangerDoNotUseSx={{ ...sharedCellSxBase, backgroundColor: bg }}
        alignItems="center"
        gap="0"
      >
        <Flex
          flex={1}
          height={PROPERTY_GRID_ROW_HEIGHT}
          alignItems="center"
          padding="4px 8px"
          __dangerDoNotUseSx={{
            borderRight: `1px solid ${theme.colors.strokeDefault}`,
          }}
        >
          <Typography variant="body1">{`${(entry.spreadRatio * 100).toFixed(2)}%`}</Typography>
        </Flex>

        <Flex flex={1} height={PROPERTY_GRID_ROW_HEIGHT} alignItems="center" padding="4px 8px">
          <Typography variant="body1">
            {entry.range !== undefined ? formatDollars(entry.range) : '—'}
          </Typography>
        </Flex>
      </Flex>
    );
  };

  const renderCompRow = (entry: SensitivityAnalysisUnitRateResult, isSelected: boolean) => {
    const bg = isSelected ? theme.colors.surface8 : theme.colors.surface1;
    const rateDollars = orUndefined(schAloftPrice.parse)(entry.rate)?.toDollarLike();

    let adjustedPriceStr = '—';
    if (
      salePriceDollars !== undefined &&
      varValue !== undefined &&
      subjectVarValue !== undefined &&
      rateDollars !== undefined
    ) {
      const adjusted = salePriceDollars - (varValue - subjectVarValue) * rateDollars;
      adjustedPriceStr = formatDollars(adjusted);
    }

    return (
      <Typography
        key={entry.rate?.unitAmount}
        variant="body1"
        __dangerDoNotUseSx={{ ...sharedCellSxBase, backgroundColor: bg }}
      >
        {adjustedPriceStr}
      </Typography>
    );
  };

  // Header row
  const headerBg = theme.colors.surface7;
  const headerSx = {
    ...sharedCellSxBase,
    backgroundColor: headerBg,
    fontWeight: 'bold',
  };

  const headerRow = isSubject ? (
    <Flex __dangerDoNotUseSx={headerSx} alignItems="center" gap="0">
      <Flex
        flex={1}
        height={PROPERTY_GRID_ROW_HEIGHT}
        alignItems="center"
        padding="4px 8px"
        __dangerDoNotUseSx={{
          borderRight: `1px solid ${theme.colors.strokeDefault}`,
        }}
      >
        <Typography variant="label">% Variance</Typography>
      </Flex>
      <Flex flex={1} height={PROPERTY_GRID_ROW_HEIGHT} alignItems="center" padding="4px 8px">
        <Typography variant="label">Max - Min</Typography>
      </Flex>
    </Flex>
  ) : (
    <Typography variant="label" __dangerDoNotUseSx={headerSx}>
      Adjusted Price
    </Typography>
  );

  return (
    <>
      {headerRow}
      {sensitivityResult.testedUnitRates.map((entry) => {
        const isSelected = entry.rate?.unitAmount === selectedRateUnitAmount;
        return isSubject ? renderSubjectRow(entry, isSelected) : renderCompRow(entry, isSelected);
      })}
    </>
  );
};
